import axios from 'axios';
import { setAlert } from './alert';
import { 
    GET_DELIVERY_CAN_TRANSACTIONS,
    GET_DELIVERY_CAN_TRANSACTION,
    DELIVERY_CAN_TRANSACTION_LOADING,
    DELIVERY_CAN_TRANSACTION_ERROR,
    UPDATE_DELIVERY_CAN_TRANSACTION,
    CLEAR_DELIVERY_CAN_TRANSACTIONS
} from './types';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

/**
 * Get all delivery can transactions with optional query parameters
 * @param {string} query - Query string with filters
 */
export const getDeliveryCanTransactions = (query = '') => async dispatch => {
    try {
        // Set loading state
        dispatch({ type: DELIVERY_CAN_TRANSACTION_LOADING });

        const res = await axios.get(`${API_URL}/delivery/can-transactions/all${query ? '?' + query : ''}`);
        
        if (res.data.error === false) {
            dispatch({
                type: GET_DELIVERY_CAN_TRANSACTIONS,
                payload: {
                    transactions: res.data.data.transactions,
                    pages: res.data.data.pages,
                    count: res.data.data.count,
                    currentPage: extractPageFromQuery(query)
                }
            });
        } else {
            dispatch({ 
                type: DELIVERY_CAN_TRANSACTION_ERROR,
                payload: res.data.message || 'Failed to fetch delivery can transactions'
            });
            dispatch(setAlert(res.data.message || 'Failed to fetch delivery can transactions', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching delivery can transactions:', err);
        dispatch({ 
            type: DELIVERY_CAN_TRANSACTION_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to fetch delivery can transactions', 'danger'));
    }
};

/**
 * Get a single delivery can transaction by ID
 * @param {string} id - Transaction ID
 */
export const getDeliveryCanTransaction = (id) => async dispatch => {
    try {
        dispatch({ type: DELIVERY_CAN_TRANSACTION_LOADING });

        const res = await axios.get(`${API_URL}/delivery/can-transactions/all/${id}`);
        
        if (res.data.error === false) {
            dispatch({
                type: GET_DELIVERY_CAN_TRANSACTION,
                payload: res.data.data
            });
        } else {
            dispatch({ 
                type: DELIVERY_CAN_TRANSACTION_ERROR,
                payload: res.data.message || 'Failed to fetch transaction details'
            });
            dispatch(setAlert(res.data.message || 'Failed to fetch transaction details', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching delivery can transaction:', err);
        dispatch({ 
            type: DELIVERY_CAN_TRANSACTION_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to fetch transaction details', 'danger'));
    }
};

/**
 * Update delivery can transaction status
 * @param {string} id - Transaction ID
 * @param {string} status - New status
 * @param {object} additionalData - Additional update data
 */
export const updateDeliveryCanTransaction = (id, status, additionalData = {}) => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ 
            status,
            ...additionalData
        });
        
        const res = await axios.put(`${API_URL}/delivery/can-transactions/all/${id}`, body, config);
        
        if (res.data.error === false) {
            dispatch({
                type: UPDATE_DELIVERY_CAN_TRANSACTION,
                payload: res.data.data
            });
            
            const statusMessages = {
                'pending': 'Transaction marked as pending',
                'assigned': 'Transaction assigned to agent',
                'in-progress': 'Transaction marked as in progress',
                'delivered': 'Transaction marked as delivered',
                'cancelled': 'Transaction has been cancelled',
                'failed': 'Transaction has been marked as failed'
            };
            
            dispatch(setAlert(statusMessages[status] || 'Transaction status updated', 'success'));
        } else {
            dispatch({ 
                type: DELIVERY_CAN_TRANSACTION_ERROR,
                payload: res.data.message || 'Failed to update transaction status'
            });
            dispatch(setAlert(res.data.message || 'Failed to update transaction status', 'danger'));
        }
    } catch (err) {
        console.error('Error updating delivery can transaction:', err);
        dispatch({ 
            type: DELIVERY_CAN_TRANSACTION_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to update transaction status', 'danger'));
    }
};

/**
 * Cancel a delivery can transaction
 * @param {string} id - Transaction ID
 * @param {string} reason - Cancellation reason
 */
export const cancelDeliveryCanTransaction = (id, reason) => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ 
            status: 'cancelled',
            cancellationReason: reason
        });
        
        const res = await axios.put(`${API_URL}/delivery/transactions/${id}/cancel`, body, config);
        
        if (res.data.error === false) {
            dispatch({
                type: UPDATE_DELIVERY_CAN_TRANSACTION,
                payload: res.data.data
            });
            dispatch(setAlert('Transaction cancelled successfully', 'success'));
        } else {
            dispatch({ 
                type: DELIVERY_CAN_TRANSACTION_ERROR,
                payload: res.data.message || 'Failed to cancel transaction'
            });
            dispatch(setAlert(res.data.message || 'Failed to cancel transaction', 'danger'));
        }
    } catch (err) {
        console.error('Error cancelling delivery can transaction:', err);
        dispatch({ 
            type: DELIVERY_CAN_TRANSACTION_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to cancel transaction', 'danger'));
    }
};

/**
 * Clear delivery can transactions data
 */
export const clearDeliveryCanTransactions = () => dispatch => {
    dispatch({ type: CLEAR_DELIVERY_CAN_TRANSACTIONS });
};

/**
 * Helper function to extract page number from query string
 * @param {string} query - Query string
 * @returns {number} Page number
 */
const extractPageFromQuery = (query) => {
    if (!query) return 1;
    const pageMatch = query.match(/page=(\d+)/);
    return pageMatch ? parseInt(pageMatch[1]) : 1;
};

/**
 * Convert delivery can transactions to CSV format
 * @param {Array} transactions - Transaction rows from the export endpoint
 */
const convertTransactionsToCSV = (transactions) => {
    if (!transactions || !Array.isArray(transactions) || transactions.length === 0) return '';
    const headers = [
        'Transaction ID', 'Status', 'Customer Name', 'Customer Phone',
        'Serviceable Area', 'Sector', 'Product Name', 'Product Brand',
        'Filled Cans', 'Empty Cans', 'Damage Cans', 'Deposit Used',
        'Agent Name', 'Request Time', 'Assigned Time', 'Delivered Time'
    ];

    const escapeCSV = (val) => {
        if (val === null || val === undefined) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
    };

    const rows = transactions.map(t => [
        escapeCSV(t.transactionId),
        escapeCSV(t.status),
        escapeCSV(t.userName),
        escapeCSV(t.userPhone),
        escapeCSV(t.serviceableAreaName),
        escapeCSV(t.sectorName),
        escapeCSV(t.productName),
        escapeCSV(t.productBrand),
        escapeCSV(t.filledCans),
        escapeCSV(t.emptyCans),
        escapeCSV(t.damageCans),
        escapeCSV(t.depositUsed),
        escapeCSV(t.agentName),
        escapeCSV(t.requestTime ? new Date(t.requestTime).toLocaleString() : ''),
        escapeCSV(t.assignedTime ? new Date(t.assignedTime).toLocaleString() : ''),
        escapeCSV(t.deliveredTime ? new Date(t.deliveredTime).toLocaleString() : '')
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
};

/**
 * Export delivery can transactions as a filtered, unpaginated CSV download
 * @param {string} filters - Query string with filters (page param is stripped)
 */
export const exportDeliveryCanTransactions = (filters = '') => async dispatch => {
    try {
        const cleanFilters = filters
            .split('&')
            .filter(param => !param.startsWith('page='))
            .join('&');

        const res = await axios.get(`${API_URL}/delivery/can-transactions/all/export?${cleanFilters}`);

        if (res.data.error === false) {
            const transactions = res.data.data.transactions;
            const csvContent = convertTransactionsToCSV(transactions);

            if (!csvContent) {
                dispatch(setAlert('No transactions found to export', 'warning', 3000));
                return;
            }

            const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `delivery-transactions-export-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            dispatch(setAlert('Delivery transactions exported successfully', 'success', 3000));
        } else {
            dispatch(setAlert(res.data.message || 'Failed to export delivery transactions', 'danger'));
        }
    } catch (err) {
        console.error('Error exporting delivery can transactions:', err);
        dispatch(setAlert(err.response?.data?.message || 'Failed to export delivery transactions', 'danger'));
    }
};

/**
 * Search delivery can transactions by various filters
 * @param {object} filters - Search filters
 */
export const searchDeliveryCanTransactions = (filters) => async dispatch => {
    try {
        dispatch({ type: DELIVERY_CAN_TRANSACTION_LOADING });

        // Build query string from filters
        const queryParams = new URLSearchParams();
        
        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key] !== '') {
                queryParams.append(key, filters[key]);
            }
        });

        const res = await axios.get(`${API_URL}/delivery/transactions/search?${queryParams.toString()}`);
        
        if (res.data.error === false) {
            dispatch({
                type: GET_DELIVERY_CAN_TRANSACTIONS,
                payload: {
                    transactions: res.data.data.transactions,
                    pages: res.data.data.pages,
                    count: res.data.data.count,
                    currentPage: filters.page || 1
                }
            });
        } else {
            dispatch({ 
                type: DELIVERY_CAN_TRANSACTION_ERROR,
                payload: res.data.message || 'Failed to search transactions'
            });
            dispatch(setAlert(res.data.message || 'Failed to search transactions', 'danger'));
        }
    } catch (err) {
        console.error('Error searching delivery can transactions:', err);
        dispatch({ 
            type: DELIVERY_CAN_TRANSACTION_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to search transactions', 'danger'));
    }
};
